import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SaferSavvyLogo from '../components/SaferSavvyLogo';
import { updateSEO } from '../utils/seo';

const OTP_LENGTH = 6;

const EyeOpen = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOff = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const inputCls = 'w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D5F9E]/30 focus:border-[#2D5F9E] transition-all';

const ForgotPassword: React.FC = () => {
  const [step, setStep]                           = useState<1 | 2 | 3>(1);
  const [email, setEmail]                         = useState('');
  const [otpDigits, setOtpDigits]                 = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [newPassword, setNewPassword]             = useState('');
  const [confirmPassword, setConfirmPassword]     = useState('');
  const [showNewPw, setShowNewPw]                 = useState(false);
  const [showConfirmPw, setShowConfirmPw]         = useState(false);
  const [error, setError]                         = useState('');
  const [success, setSuccess]                     = useState('');
  const [userId, setUserId]                       = useState('');
  const [isLoading, setIsLoading]                 = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  const navigate  = useNavigate();
  const otpRefs   = useRef<(HTMLInputElement | null)[]>([]);

  /* ── SEO: Forgot Password page meta ── */
  useEffect(() => {
    updateSEO({
      title: "Forgot Password — Reset Your SaferSavvy Account",
      description:
        "Reset your SaferSavvy account password securely. Enter your registered email to receive a verification code and set a new password.",
      canonical: "/forgot-password",
      noIndex: true,
    });
  }, []);

  /* ── OTP input handlers ── */
  const handleOtpChange = (i: number, v: string) => {
    if (!/^\d*$/.test(v)) return;
    const d = [...otpDigits]; d[i] = v.slice(-1); setOtpDigits(d);
    if (v && i < OTP_LENGTH - 1) otpRefs.current[i + 1]?.focus();
  };
  const handleOtpKeyDown = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };
  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!p) return;
    const d = Array(OTP_LENGTH).fill(''); p.split('').forEach((c, i) => { d[i] = c; });
    setOtpDigits(d); otpRefs.current[Math.min(p.length, OTP_LENGTH - 1)]?.focus();
  };

  /* ── Step 1: send OTP ── */
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email'); return; }
    setIsLoading(true); setError('');
    try {
      const res = await axios.post(`${import.meta.env.VITE_API_BASE_URL}api/auth/forget-password`, { email });
      if (res.status === 200) {
        setUserId(res.data.userId);
        setSuccess('OTP sent to your email');
        setTimeout(() => { setStep(2); setSuccess(''); setTimeout(() => otpRefs.current[0]?.focus(), 100); }, 1500);
      }
    } catch (err: any) {
      const m: Record<number, string> = { 400: 'Invalid email format.', 404: 'No account found with this email.', 429: 'Too many attempts. Try again later.', 500: 'Server error. Try again later.' };
      setError(err.response ? (m[err.response.status] || err.response.data?.message || 'Failed to send OTP') : 'No response from server.');
    } finally { setIsLoading(false); }
  };

  /* ── Step 2: verify OTP ── */
  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = otpDigits.join('');
    if (code.length < OTP_LENGTH) { setError('Please enter the complete 6-digit code'); return; }
    setIsLoading(true); setError('');
    try {
      const res = await axios.post(`${import.meta.env.VITE_API_BASE_URL}api/auth/verify-otp`, { userId, otp: code, purpose: 'forgotPassword' });
      if (res.status === 200) {
        setUserId(res.data.userId || userId);
        setSuccess('OTP verified successfully');
        setTimeout(() => { setStep(3); setSuccess(''); }, 1500);
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        const left = 5 - (err.response.data?.attempts || 1);
        setAttemptsRemaining(left);
        setError(left <= 0 ? 'Too many attempts. OTP locked for 30 minutes.' : `Incorrect OTP. ${left} attempt${left !== 1 ? 's' : ''} remaining.`);
      } else {
        setError(err.response?.data?.message || 'OTP verification failed.');
      }
    } finally { setIsLoading(false); }
  };

  /* ── Step 3: reset password ── */
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) { setError('Please fill in all fields'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    setIsLoading(true); setError('');
    try {
      const res = await axios.put(`${import.meta.env.VITE_API_BASE_URL}api/auth/reset-password`, { userId, newPassword });
      if (res.status === 200) {
        setSuccess('Password reset! Redirecting to login…');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Password reset failed.');
    } finally { setIsLoading(false); }
  };

  const handleResendOtp = async () => {
    setIsLoading(true); setError('');
    try {
      const res = await axios.post(`${import.meta.env.VITE_API_BASE_URL}api/auth/resend-otp`, { userId });
      if (res.status === 200) { setOtpDigits(Array(OTP_LENGTH).fill('')); setSuccess('New OTP sent'); setAttemptsRemaining(5); setTimeout(() => { setSuccess(''); otpRefs.current[0]?.focus(); }, 1500); }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resend OTP.');
    } finally { setIsLoading(false); }
  };

  /* ── Left panel copy per step ── */
  const leftHeading = [
    <>Forgot your<br />password?</>,
    <>Check your<br /><span style={{ color: '#E3B02E' }}>inbox.</span></>,
    <>Create a new<br /><span style={{ color: '#E3B02E' }}>password.</span></>,
  ][step - 1];

  const leftDesc = [
    "Enter your registered email and we'll send a one-time code to help you reset it.",
    'Enter the 6-digit verification code we just sent to your email address.',
    'Your identity is verified. Choose a strong new password for your account.',
  ][step - 1];

  /* ── Step pill ── */
  const steps = ['Email', 'Verify', 'Reset'];

  return (
    <div className="h-screen flex overflow-hidden">

      {/* ── Left panel ── */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] bg-[#1D3047] flex-col px-10 xl:px-14 py-8 overflow-hidden"
        style={{ backgroundImage: 'radial-gradient(ellipse at 20% 60%, rgba(45,95,158,0.45) 0%, transparent 70%)' }}>
        <div className="flex items-center gap-3 flex-shrink-0">
          <SaferSavvyLogo size={48} />
          <span className="text-white font-bold text-xl tracking-tight">
            Safer<span style={{ color: '#E3B02E' }}>Savvy</span>
          </span>
        </div>

        <div className="flex-1 flex flex-col justify-center mb-5 min-h-0">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
              <h1 className="text-4xl xl:text-5xl font-black text-white leading-tight mb-4">
                {leftHeading}
              </h1>
              <p className="text-blue-200 text-sm xl:text-base leading-relaxed max-w-sm">{leftDesc}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        <img src="/login-busimg.png" alt="Bus" className="w-full mb-6 rounded-2xl object-cover flex-shrink-0" style={{ maxHeight: '220px' }} />
        <p className="text-blue-300/50 text-xs flex-shrink-0">© 2026 SaferSavvy</p>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 bg-white flex flex-col overflow-y-auto">
        {/* Top bar */}
        <div className="flex justify-end px-8 py-3">
          <span className="text-sm text-gray-500">
            Remember it?{' '}
            <Link to="/login" className="font-semibold text-[#1D3047] hover:text-[#E3B02E] transition-colors">Sign in</Link>
          </span>
        </div>

        <div className="flex-1 flex items-center justify-center px-8 sm:px-12 lg:px-16 xl:px-20 py-4">
          <div className="w-full max-w-md">

            {/* Step indicator */}
            <div className="flex items-center gap-1 mb-8">
              {steps.map((label, idx) => {
                const n = idx + 1;
                const done    = step > n;
                const active  = step === n;
                return (
                  <React.Fragment key={label}>
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all text-xs font-black
                        ${done   ? 'bg-[#1D3047]'
                        : active ? 'bg-[#E3B02E]'
                        :          'bg-gray-100 border border-gray-200'}`}>
                        {done ? (
                          <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                          </svg>
                        ) : (
                          <span className={active ? 'text-white' : 'text-gray-400'}>{n}</span>
                        )}
                      </div>
                      <span className={`text-xs font-medium ${active ? 'text-[#1D3047]' : done ? 'text-gray-500' : 'text-gray-300'}`}>{label}</span>
                    </div>
                    {idx < steps.length - 1 && (
                      <div className={`flex-1 h-px mx-1 transition-all ${step > n ? 'bg-[#1D3047]/40' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <AnimatePresence mode="wait">

              {/* ── STEP 1: Email ── */}
              {step === 1 && (
                <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                  <div className="relative w-14 h-14 mx-auto mb-5">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg shadow-[#1D3047]/20"
                      style={{ background: 'linear-gradient(135deg, #1D3047 0%, #2D5F9E 100%)' }}>
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"/>
                      </svg>
                    </div>
                  </div>
                  <h2 className="text-2xl font-black text-[#1D3047] text-center mb-1">Password recovery</h2>
                  <p className="text-gray-500 text-sm text-center mb-6">We'll send a 6-digit code to verify it's you</p>

                  {error && <Alert type="error" msg={error} onClose={() => setError('')} />}
                  {success && <Alert type="success" msg={success} />}

                  <form onSubmit={handleEmailSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Registered email</label>
                      <input type="email" placeholder="you@university.edu" value={email}
                        onChange={e => setEmail(e.target.value)} className={inputCls} />
                    </div>
                    <button type="submit" disabled={isLoading}
                      className="w-full bg-[#1D3047] hover:bg-[#2D5F9E] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-sm">
                      {isLoading ? 'Sending code…' : 'Send verification code'}
                    </button>
                  </form>
                  <p className="text-center text-sm text-gray-500 mt-5">
                    Remember it?{' '}
                    <Link to="/login" className="font-semibold text-[#1D3047] hover:text-[#E3B02E] transition-colors">Sign in</Link>
                  </p>
                </motion.div>
              )}

              {/* ── STEP 2: OTP ── */}
              {step === 2 && (
                <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                  <div className="relative w-14 h-14 mx-auto mb-5">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg shadow-[#1D3047]/20"
                      style={{ background: 'linear-gradient(135deg, #1D3047 0%, #2D5F9E 100%)' }}>
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                    </div>
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#E3B02E] rounded-full border-2 border-white" />
                  </div>
                  <h2 className="text-2xl font-black text-[#1D3047] text-center mb-1">Enter the code</h2>
                  <p className="text-gray-500 text-sm text-center mb-2">6-digit code sent to</p>
                  <div className="flex justify-center mb-6">
                    <span className="inline-flex items-center gap-2 bg-[#1D3047]/6 border border-[#1D3047]/12 rounded-full px-4 py-1.5 text-sm font-semibold text-[#1D3047]">
                      {email}
                    </span>
                  </div>

                  {error && <Alert type="error" msg={error} onClose={() => setError('')} />}
                  {success && <Alert type="success" msg={success} />}

                  <form onSubmit={handleOtpSubmit}>
                    <div className="flex justify-center gap-2.5 mb-5">
                      {otpDigits.map((d, i) => (
                        <input key={i}
                          ref={el => { otpRefs.current[i] = el; }}
                          type="text" inputMode="numeric" maxLength={1} value={d}
                          onChange={e => handleOtpChange(i, e.target.value)}
                          onKeyDown={e => handleOtpKeyDown(i, e)}
                          onPaste={i === 0 ? handleOtpPaste : undefined}
                          className={`w-11 h-14 text-center text-2xl font-black rounded-xl border-2 focus:outline-none transition-all duration-150
                            ${d ? 'border-[#E3B02E] bg-[#E3B02E]/8 text-[#1D3047]' : 'border-gray-200 bg-gray-50'}
                            focus:border-[#1D3047] focus:bg-white focus:shadow-sm`}
                        />
                      ))}
                    </div>

                    <div className="flex justify-center mb-5">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        Didn't receive it?{' '}
                        <button type="button" onClick={handleResendOtp} disabled={isLoading || attemptsRemaining <= 0}
                          className="font-semibold text-[#1D3047] hover:text-[#E3B02E] transition-colors disabled:opacity-40">
                          {isLoading ? 'Sending…' : 'Resend code'}
                        </button>
                      </div>
                    </div>

                    <button type="submit" disabled={isLoading || otpDigits.join('').length < OTP_LENGTH || attemptsRemaining <= 0}
                      className="w-full bg-[#1D3047] hover:bg-[#2D5F9E] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm">
                      {isLoading ? 'Verifying…' : 'Verify & continue'}
                    </button>
                  </form>

                  <button type="button" onClick={() => { setStep(1); setOtpDigits(Array(OTP_LENGTH).fill('')); setError(''); }}
                    className="w-full flex items-center justify-center gap-1.5 text-sm text-gray-400 hover:text-[#1D3047] font-medium mt-5 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
                    </svg>
                    Back to email
                  </button>
                </motion.div>
              )}

              {/* ── STEP 3: New password ── */}
              {step === 3 && (
                <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg shadow-[#1D3047]/20 mx-auto mb-5"
                    style={{ background: 'linear-gradient(135deg, #1D3047 0%, #2D5F9E 100%)' }}>
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                    </svg>
                  </div>
                  <h2 className="text-2xl font-black text-[#1D3047] text-center mb-1">Set new password</h2>
                  <p className="text-gray-500 text-sm text-center mb-6">Choose a strong password — at least 6 characters</p>

                  {error && <Alert type="error" msg={error} onClose={() => setError('')} />}
                  {success && <Alert type="success" msg={success} />}

                  <form onSubmit={handlePasswordSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">New password</label>
                      <div className="relative">
                        <input type={showNewPw ? 'text' : 'password'} placeholder="Create a password"
                          value={newPassword} onChange={e => setNewPassword(e.target.value)}
                          className={inputCls + ' pr-11'} />
                        <button type="button" tabIndex={-1} onClick={() => setShowNewPw(p => !p)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showNewPw ? <EyeOff /> : <EyeOpen />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Confirm password</label>
                      <div className="relative">
                        <input type={showConfirmPw ? 'text' : 'password'} placeholder="Repeat your password"
                          value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                          className={inputCls + ' pr-11'} />
                        <button type="button" tabIndex={-1} onClick={() => setShowConfirmPw(p => !p)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showConfirmPw ? <EyeOff /> : <EyeOpen />}
                        </button>
                      </div>
                      {confirmPassword && newPassword !== confirmPassword && (
                        <p className="mt-1 text-xs text-red-500">Passwords don't match</p>
                      )}
                    </div>
                    <button type="submit" disabled={isLoading}
                      className="w-full bg-[#1D3047] hover:bg-[#2D5F9E] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-sm">
                      {isLoading ? 'Resetting…' : 'Reset password'}
                    </button>
                  </form>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Reusable alert ── */
const Alert: React.FC<{ type: 'error' | 'success'; msg: string; onClose?: () => void }> = ({ type, msg, onClose }) => (
  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
    className={`flex items-center justify-between text-sm rounded-xl px-4 py-2.5 mb-4 ${
      type === 'error' ? 'bg-red-50 border border-red-200 text-red-600' : 'bg-green-50 border border-green-200 text-green-600'
    }`}>
    <span>{msg}</span>
    {onClose && <button onClick={onClose} className="ml-3 font-bold text-lg leading-none opacity-60 hover:opacity-100">×</button>}
  </motion.div>
);

export default ForgotPassword;
